import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Project, Session } from '@claude-code-api/shared';
import { MessageSquareText, ChevronRight, Layers3, FolderGit2, Clock3 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { useInterval } from '@/hooks/useInterval';
import { isRunningPmSession } from '@/lib/gift-agent';

interface TalkProjectGroup {
  project: Project;
  sessions: Session[];
  latestStartedAt: number;
}

function formatElapsed(date: Date | string): string {
  const created = new Date(date);
  const diffMs = Date.now() - created.getTime();
  const minutes = Math.max(0, Math.floor(diffMs / 60000));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h active`;
  if (hours > 0) return `${hours}h ${minutes % 60}m active`;
  return `${minutes}m active`;
}

export default function TalkPage() {
  const { t } = useTranslation('session');
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [pickerProject, setPickerProject] = useState<TalkProjectGroup | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [projectsRes, sessionsRes] = await Promise.all([
        fetch('/api/projects', { credentials: 'include' }),
        fetch('/api/sessions', { credentials: 'include' }),
      ]);

      if (!projectsRes.ok || !sessionsRes.ok) {
        throw new Error('Failed to load running agent sessions');
      }

      const [projectsData, sessionsData] = await Promise.all([projectsRes.json(), sessionsRes.json()]);
      setProjects(projectsData.data || []);
      setSessions(sessionsData.data || []);
    } catch (error) {
      console.error('Failed to load talk data:', error);
      toast.error('Failed to load Talk. Please retry.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useInterval(() => {
    void fetchData();
  }, 10000);

  const talkProjects = useMemo<TalkProjectGroup[]>(() => {
    const runningSessions = sessions.filter(isRunningPmSession);
    const sessionsByProject = new Map<string, Session[]>();

    for (const session of runningSessions) {
      const current = sessionsByProject.get(session.projectId) || [];
      current.push(session);
      sessionsByProject.set(session.projectId, current);
    }

    return projects
      .map((project) => {
        const projectSessions = (sessionsByProject.get(project.id) || []).sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        if (projectSessions.length === 0) return null;
        return {
          project,
          sessions: projectSessions,
          latestStartedAt: new Date(projectSessions[0].createdAt).getTime(),
        };
      })
      .filter((value): value is TalkProjectGroup => Boolean(value))
      .sort((a, b) => b.latestStartedAt - a.latestStartedAt);
  }, [projects, sessions]);

  const openProjectTalk = useCallback((group: TalkProjectGroup) => {
    if (group.sessions.length === 1) {
      navigate(`/talk/${group.sessions[0].id}`);
      return;
    }
    setPickerProject(group);
  }, [navigate]);

  return (
    <>
      <div className="space-y-4 pb-20 md:pb-6">
        <section className="sticky top-14 z-20 -mx-4 border-b bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-primary">Talk</p>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight">Jump straight into PM chat</h1>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                Only projects with a running GIFT Agent session appear here.
              </p>
            </div>
            <Button variant="outline" size="sm" asChild className="hidden sm:inline-flex">
              <Link to="/sessions">View all sessions</Link>
            </Button>
          </div>
        </section>

        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((index) => (
              <div key={index} className="rounded-2xl border bg-card p-4 shadow-sm">
                <Skeleton className="h-5 w-28" />
                <Skeleton className="mt-3 h-4 w-48" />
                <Skeleton className="mt-4 h-12 w-full rounded-xl" />
              </div>
            ))}
          </div>
        ) : talkProjects.length === 0 ? (
          <div className="rounded-3xl border border-dashed bg-card/60 px-5 py-8 text-center shadow-sm">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <MessageSquareText className="h-6 w-6" />
            </div>
            <h2 className="mt-4 text-lg font-semibold">No running PM chats yet</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Start a GIFT Agent session from a project first. Once it is running, it will appear here.
            </p>
            <div className="mt-5 flex justify-center gap-2">
              <Button asChild>
                <Link to="/projects">Open projects</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/sessions">Check sessions</Link>
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {talkProjects.map((group) => {
              const primarySession = group.sessions[0];
              const multi = group.sessions.length > 1;
              return (
                <button
                  key={group.project.id}
                  type="button"
                  onClick={() => openProjectTalk(group)}
                  className="group w-full rounded-3xl border bg-card p-4 text-left shadow-sm transition hover:border-primary/40 hover:bg-card/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <FolderGit2 className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h2 className="truncate text-base font-semibold">{group.project.name}</h2>
                            <StatusIndicator status="running" size="sm" />
                          </div>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {multi
                              ? `${group.sessions.length} running agent sessions ready for direct PM chat`
                              : 'One running agent session ready for direct PM chat'}
                          </p>
                        </div>
                        <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition group-hover:text-primary" />
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="secondary" className="gap-1 rounded-full px-2.5 py-1 text-[11px]">
                          <MessageSquareText className="h-3.5 w-3.5" />
                          {primarySession.runnerType === 'acpx-experimental' ? t('runnerLabels.acpx-experimental') : t('runnerLabels.acpx')}
                        </Badge>
                        {multi && (
                          <Badge variant="outline" className="gap-1 rounded-full px-2.5 py-1 text-[11px]">
                            <Layers3 className="h-3.5 w-3.5" />
                            Pick a session
                          </Badge>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <Clock3 className="h-3.5 w-3.5" />
                          {formatElapsed(primarySession.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!pickerProject} onOpenChange={(open) => !open && setPickerProject(null)}>
        <DialogContent className="max-w-md rounded-3xl p-0 sm:rounded-3xl">
          <DialogHeader className="border-b px-5 py-4 text-left">
            <DialogTitle className="text-lg">Choose a running session</DialogTitle>
            <DialogDescription>
              {pickerProject ? `Project: ${pickerProject.project.name}` : 'Pick which PM session to open.'}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[70vh] space-y-2 overflow-y-auto px-4 py-4">
            {pickerProject?.sessions.map((session, index) => (
              <button
                key={session.id}
                type="button"
                onClick={() => {
                  setPickerProject(null);
                  navigate(`/talk/${session.id}`);
                }}
                className="w-full rounded-2xl border bg-card px-4 py-3 text-left transition hover:border-primary/40 hover:bg-muted/40"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium">Session {index + 1}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {session.runnerType === 'acpx-experimental' ? t('pmRuntime.experimental') : t('pmRuntime.standard')}
                    </p>
                  </div>
                  <Badge variant="secondary">{formatElapsed(session.createdAt)}</Badge>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
